import React, { Component } from "react";
import ReactDOM from "react-dom";

// Design-system multi-select dropdown ("All (N)" style). Drop-in replacement for
// components/common/CheckboxDropdown — same props: options [{value,label}],
// value [ids], onChange(ids), placeholder. Styled via _ui.scss (.ds-dd*).
// Uses ReactDOM.createPortal so it is never clipped by scrollable / overflow-hidden
// containers such as a Bootstrap modal body.
export default class CheckboxDropdown extends Component {
  constructor(props) {
    super(props);
    this.state = { open: false, rect: null };
    this.triggerRef = React.createRef();
    this.panelRef = React.createRef();
  }

  componentDidMount() {
    document.addEventListener("mousedown", this.handleOutside);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleOutside);
    window.removeEventListener("scroll", this.reposition, true);
    window.removeEventListener("resize", this.reposition);
  }

  handleOutside = (e) => {
    const inTrigger = this.triggerRef.current && this.triggerRef.current.contains(e.target);
    const inPanel = this.panelRef.current && this.panelRef.current.contains(e.target);
    if (!inTrigger && !inPanel) this.close();
  };

  open = () => {
    if (!this.triggerRef.current) return;
    const rect = this.triggerRef.current.getBoundingClientRect();
    this.setState({ open: true, rect });
    window.addEventListener("scroll", this.reposition, true);
    window.addEventListener("resize", this.reposition);
  };

  close = () => {
    this.setState({ open: false });
    window.removeEventListener("scroll", this.reposition, true);
    window.removeEventListener("resize", this.reposition);
  };

  toggleOpen = () => {
    this.state.open ? this.close() : this.open();
  };

  reposition = () => {
    if (this.state.open && this.triggerRef.current) {
      this.setState({ rect: this.triggerRef.current.getBoundingClientRect() });
    }
  };

  getNormalizedValue = () => {
    const { value } = this.props;
    if (!value) return [];
    if (Array.isArray(value)) return value.map(v => String(v));
    return [String(value)];
  };

  toggle = (id) => {
    const { onChange } = this.props;
    const normValue = this.getNormalizedValue();
    const strId = String(id);
    const updated = normValue.includes(strId)
      ? normValue.filter((v) => String(v) !== strId)
      : [...normValue, strId];
    if (onChange) onChange(updated);
  };

  render() {
    const { options = [], placeholder = "Select..." } = this.props;
    const { open, rect } = this.state;
    const normValue = this.getNormalizedValue();

    const selectedOptions = options.filter(o => normValue.includes(String(o.value)));
    const isAllSelected = options.length > 0 && selectedOptions.length === options.length;

    const label =
      selectedOptions.length === 0
        ? placeholder
        : selectedOptions.length === options.length
          ? `All (${options.length})`
          : selectedOptions.length === 1
            ? selectedOptions[0]?.label || `${selectedOptions.length} selected`
            : `${selectedOptions.length} selected`;

    const panel = open && rect ? ReactDOM.createPortal(
      <div
        ref={this.panelRef}
        className="ds-dd-menu"
        style={{
          position: "fixed",
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width,
          zIndex: 999999,
          display: "block",
          maxHeight: "260px",
          overflowY: "auto",
          background: "#ffffff",
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.16)",
          borderRadius: "8px",
          border: "1px solid #e2e8f0",
          padding: "6px"
        }}
      >
        {options.length === 0 ? (
          <div className="ds-dd-empty" style={{ padding: "8px 12px", color: "#64748b", fontSize: "13px" }}>No options</div>
        ) : (
          <>
            <div
              className="ds-dd-all"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "6px 10px",
                cursor: "pointer",
                borderRadius: "4px",
                fontWeight: 600,
                fontSize: "13px",
                color: "#1e293b",
                borderBottom: "1px solid #f1f5f9",
                marginBottom: "4px"
              }}
              onClick={() => {
                if (this.props.onChange) {
                  this.props.onChange(isAllSelected ? [] : options.map((o) => String(o.value)));
                }
              }}
            >
              <input type="checkbox" readOnly checked={isAllSelected} style={{ cursor: "pointer" }} />
              Select all
            </div>
            {options.map((o) => {
              const strVal = String(o.value);
              const sel = normValue.includes(strVal);
              return (
                <div
                  key={o.value}
                  className={`ds-dd-opt ${sel ? "is-sel" : ""}`.trim()}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "6px 10px",
                    cursor: "pointer",
                    borderRadius: "4px",
                    fontSize: "13px",
                    color: sel ? "#2563eb" : "#334155",
                    background: sel ? "#eff6ff" : "transparent"
                  }}
                  onClick={() => this.toggle(o.value)}
                >                  <input type="checkbox" readOnly checked={sel} style={{ cursor: "pointer" }} />
                  <span>{o.label}</span>
                </div>
              );
            })}
          </>
        )}
      </div>,
      document.body
    ) : null;

    return (
      <div className="ds-dd" style={{ width: "100%" }}>
        <div
          ref={this.triggerRef}
          className="ds-dd-btn"
          data-empty={selectedOptions.length === 0}
          onClick={this.toggleOpen}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "6px 12px",
            minHeight: "34px",
            background: "#ffffff",
            border: "1px solid #cbd5e1",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "13px",
            color: selectedOptions.length === 0 ? "#94a3b8" : "#1e293b"
          }}
        >
          <span className="ds-dd-label" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
          <svg
            className="ds-chev"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            style={{
              width: "12px",
              height: "12px",
              minWidth: "12px",
              minHeight: "12px",
              flexShrink: 0,
              marginLeft: "8px",
              transform: open ? "rotate(180deg)" : "none",
              transition: "transform .15s"
            }}
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </div>
        {panel}
      </div>
    );
  }
}
